import store from 'store/Store';
import OrderActions from 'actions/OrderActions';
import checkoutApi from 'services/api/checkout';
import ErrorsUtils from 'utils/Errors';
import decorators from 'utils/decorators';
import { INTERSTICE_DELAY_MS } from 'constants/Checkout';
import { BASKET_LEVEL_MESSAGES_CONTEXTS } from 'constants/RwdBasket';
import locationUtils from 'utils/Location';
import orderUtils from 'utils/Order';
import UtilActions from 'utils/redux/Actions';
import KlarnaActions from 'actions/KlarnaActions';
import AfterpayActions from 'actions/AfterpayActions';
import PazeActions from 'actions/PazeActions';
import VenmoActions from 'actions/VenmoActions';
import GooglePayActions from 'actions/GooglePayActions';
import { toggleSelect as paypalToggleSelect, setReady as paypalSetReady } from 'actions/PayPalActions';
import pazeUtils from 'utils/Paze.js';
import PayPalCheckout from 'utils/PayPalCheckout';
import Venmo from 'utils/Venmo';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import utilityApi from 'services/api/utility';
import EditDataActions from 'actions/EditDataActions';
import FormsUtils from 'utils/Forms';
import CheckoutActions from 'actions/CheckoutActions';
import ShipAddressActions from 'actions/ShipAddressActions';
import updatePreferredZipCode from 'services/api/profile/updatePreferredZipCode';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import Storage from 'utils/localStorage/Storage';
import FrictionlessUtils from 'utils/FrictionlessCheckout';
import { SECTION_NAMES } from 'constants/frictionlessCheckout';
import cookieUtils from 'utils/Cookies';
import addressUtils from 'utils/Address';
import errorsUtils from 'utils/Errors';
import ReplacementOrderActions from 'actions/ReplacementOrderActions';
import PlaceOrderButtonEN from 'components/FrictionlessCheckout/PlaceOrderButton/locales/PlaceOrderButton_en_US';
import PlaceOrderButtonFR from 'components/FrictionlessCheckout/PlaceOrderButton/locales/PlaceOrderButton_fr_CA';

const { formatZipCode } = addressUtils;
const { setErrorsFromCheckoutAPI, isFCFinalReleaseEnabled } = FrictionlessUtils;
const { getLocalisedDataFromResource } = LanguageLocaleUtils;

const authorizationGetText = getLocalisedDataFromResource(PlaceOrderButtonEN, PlaceOrderButtonFR);

let isShippingRequestInProgress = false;
let getShippingMethodPromise = false;
const getShippingMethods = (orderId, shippingGroupId) => {
    if (!isShippingRequestInProgress) {
        isShippingRequestInProgress = true;
        getShippingMethodPromise = checkoutApi
            .getAvailableShippingMethods(orderId, shippingGroupId)
            .then(shippingData => {
                store.dispatch(OrderActions.updateShippingMethods(shippingData.shippingMethods, shippingGroupId));
                isShippingRequestInProgress = false;
            })
            .catch(errorData => {
                ErrorsUtils.collectAndValidateBackEndErrors(errorData, this);
                isShippingRequestInProgress = false;
            });
    }

    return getShippingMethodPromise;
};

const refreshCheckout = ({ isUpdateOrder = true, clearErrors = true } = {}) => {
    let getOrderDetails;
    let updateShippingMethods = false;

    if (isUpdateOrder) {
        getOrderDetails = () => {
            return decorators
                .withInterstice(
                    checkoutApi.getOrderDetails,
                    INTERSTICE_DELAY_MS
                )(orderUtils.getOrderId())
                .then(newOrderDetails => {
                    const isZeroCheckout = orderUtils.isZeroCheckout();
                    const isSDUOnlyOrder = orderUtils.isSDUOnlyOrder(newOrderDetails);
                    const hasRRC = orderUtils.hasRRC(newOrderDetails);
                    const isShippableOrder = orderUtils.isShippableOrder(newOrderDetails);
                    const { isBopisOrder } = newOrderDetails.header;

                    //UC-388 We do not have to update Shipping methods if the order belongs to any of these cases.
                    //SHP-3952: Also update shipping methods for zero-checkout orders with shippable items (e.g. 750pt BI reward baskets)
                    if ((!isZeroCheckout || (isZeroCheckout && hasRRC) || (isZeroCheckout && isShippableOrder)) && !isBopisOrder) {
                        updateShippingMethods = true;
                    }

                    if (updateShippingMethods) {
                        const shippingGroupEntries = newOrderDetails.shippingGroups.shippingGroupsEntries;
                        let shippingGroup;

                        if (locationUtils.isCheckoutGiftCardShipping()) {
                            const physicalGiftCardShippingGroup = orderUtils.getPhysicalGiftCardShippingGroup(newOrderDetails);
                            shippingGroup = physicalGiftCardShippingGroup ?? shippingGroupEntries[0].shippingGroup;
                        } else {
                            shippingGroup = shippingGroupEntries[0].shippingGroup;
                        }

                        if (!isSDUOnlyOrder) {
                            getShippingMethods(newOrderDetails.header.orderId, shippingGroup.shippingGroupId);
                        }
                    }

                    clearErrors && store.dispatch(OrderActions.clearCheckoutSectionErrors());

                    const rewardWarning = newOrderDetails?.items?.basketLevelMessages?.find(
                        msg => msg.messageContext === BASKET_LEVEL_MESSAGES_CONTEXTS.REWARD_WARNING
                    );

                    if (rewardWarning?.messages?.length) {
                        store.dispatch(
                            OrderActions.setCheckoutSectionErrors({
                                [SECTION_NAMES.BI_BENEFITS]: rewardWarning.messages.join(' ')
                            })
                        );
                    }

                    store.dispatch(OrderActions.updateOrder(newOrderDetails));
                });
        };
    } else {
        getOrderDetails = () => Promise.resolve();
    }

    return getOrderDetails;
};

const getAddressList = (profileId, shipCountry) => {
    return checkoutApi
        .getAddressBook(profileId, shipCountry)
        .then(addressBook => {
            store.dispatch(UtilActions.merge('order', 'addressList', addressBook.addressList));
            store.dispatch(OrderActions.updateAddressListWithHalAddress());
        })
        .catch(errorData => {
            ErrorsUtils.collectAndValidateBackEndErrors(errorData, this);
        });
};

// When availablePaymentMethods is not provided (e.g. RwdCheckoutMain call site),
// fall back to computing each enablement flag from orderUtils so that alternate
// payment selection is not silently cleared on page load.
const resolveEnablementFlags = (availablePaymentMethods, orderDetails) => {
    const flags = availablePaymentMethods || {};

    return {
        isAfterpayEnabledForThisOrder: flags.isAfterpayEnabledForThisOrder ?? orderUtils.isAfterpayEnabledForThisOrder(orderDetails),
        isKlarnaEnabledForThisOrder: flags.isKlarnaEnabledForThisOrder ?? orderUtils.isKlarnaEnabledForThisOrder(orderDetails),
        isPazeEnabledForThisOrder: flags.isPazeEnabledForThisOrder ?? orderUtils.isPazeEnabledForThisOrder(orderDetails),
        isVenmoEnabledForThisOrder: flags.isVenmoEnabledForThisOrder ?? orderUtils.isVenmoEnabledForThisOrder(orderDetails),
        isPayPalEnabled: flags.isPayPalEnabled ?? orderUtils.isPayPalEnabled(orderDetails)
    };
};

const initializeKlarnaIfNeeded = (klarnaWasSelected, klarnaStateBeforeToggle) => {
    // Only call backgroundInit if:
    // 1. Initial page load (klarnaWasSelected was false) - first time seeing Klarna payment group
    // 2. Refresh scenario (widget is already ready) - need to update session
    // Skip if: component just called backgroundInit OR initialization already in progress
    const shouldInitialize = (!klarnaWasSelected || klarnaStateBeforeToggle?.isReady) && !klarnaStateBeforeToggle?.isInitializing;

    if (shouldInitialize) {
        const errorMessage = authorizationGetText('authorizeErrorMessage', ['Klarna']);
        store.dispatch(KlarnaActions.backgroundInit(errorMessage));
    }
};

const checkSelectedPayment =
    (paymentOptions, availablePaymentMethods) =>
        ({ orderDetails, checkoutPath = 'checkout' }) => {
        // keep Klarna selected in order to be able to restore the option
        // when user gets back to the Payment Section

            const pendingCreditCard = store.getState().order?.pendingCreditCard;

            //dont check selected payment if we have a pending CC to process
            if (isFCFinalReleaseEnabled() && pendingCreditCard) {
                return;
            }

            const {
                isAfterpayEnabledForThisOrder, isKlarnaEnabledForThisOrder, isPazeEnabledForThisOrder, isVenmoEnabledForThisOrder, isPayPalEnabled
            } =
            resolveEnablementFlags(availablePaymentMethods, orderDetails);

            const isKlarnaSelected = orderUtils.getPaymentGroup(orderDetails, orderUtils.PAYMENT_GROUP_TYPE.KLARNA) && isKlarnaEnabledForThisOrder;
            const klarnaWasSelected = store.getState().klarna?.isSelected;
            // Capture klarnaState BEFORE toggleSelect dispatch (which always sets isReady: false)
            // Default to empty object if klarna state doesn't exist yet
            const klarnaStateBeforeToggle = store.getState().klarna || {};
            store.dispatch(KlarnaActions.toggleSelect(!!isKlarnaSelected));

            // keep Afterpay selected in order to be able to restore the option
            // when user gets back to the Payment Section
            // eslint-disable-next-line max-len
            const isAfterpaySelected = orderUtils.getPaymentGroup(orderDetails, orderUtils.PAYMENT_GROUP_TYPE.AFTERPAY) && isAfterpayEnabledForThisOrder;
            store.dispatch(AfterpayActions.toggleSelect(!!isAfterpaySelected));

            // keep Paze selected in order to be able to restore the option
            // when user gets back to the Payment Section
            const isPazeSelected = orderUtils.getPaymentGroup(orderDetails, orderUtils.PAYMENT_GROUP_TYPE.PAZE) && isPazeEnabledForThisOrder;
            store.dispatch(PazeActions.toggleSelect(!!isPazeSelected));

            const isVenmoSelected = orderUtils.getPaymentGroup(orderDetails, orderUtils.PAYMENT_GROUP_TYPE.VENMO) && isVenmoEnabledForThisOrder;
            store.dispatch(VenmoActions.toggleSelect(!!isVenmoSelected));

            const isPayPalSelected = orderUtils.getPaymentGroup(orderDetails, orderUtils.PAYMENT_GROUP_TYPE.PAYPAL) && isPayPalEnabled;
            store.dispatch(paypalToggleSelect(!!isPayPalSelected));

            if (isAfterpaySelected && checkoutPath === 'checkout') {
                store.dispatch(AfterpayActions.setReady(true));
            }

            if (isKlarnaSelected && checkoutPath === 'checkout') {
                initializeKlarnaIfNeeded(klarnaWasSelected, klarnaStateBeforeToggle);
            }

            if (isPazeSelected && checkoutPath === 'checkout') {
                store.dispatch(PazeActions.loadIframe(true));
                pazeUtils.initCheckoutWidget();
            }

            if (isPayPalSelected && checkoutPath === 'checkout') {
                if (PayPalCheckout.isPayPalCheckoutEnabledForLocale()) {
                // paypalCheckoutInstance (module-level in PayPalCheckout.js) is reset on
                // page refresh. Without re-initialization, PayPalButton.f.jsx's
                // renderPayPalButton bails early when the instance is null and the SDK
                // button never mounts. preparePaypalCheckout sets up Braintree and
                // dispatches setReady(true) via braintreeCreateSuccessCallback once the
                // SDK is loaded. On failure we fall back to dispatching setReady(true)
                // so the standard Place Order button is enabled and checkout does not
                // hang in a permanent "not ready" state.
                    PayPalCheckout.preparePaypalCheckout().catch(err => {
                        Sephora.logger.error('PayPal: Failed to initialize on checkout mount', err);
                        store.dispatch(paypalSetReady(true));
                    });
                } else {
                    store.dispatch(paypalSetReady(true));
                }
            }

            if (isVenmoSelected && checkoutPath === 'checkout') {
                Venmo.initializeVenmoCheckout();
            }
        };

const isAlternatePaymentMethodSelected = (orderDetails, availablePaymentMethods) => {
    const {
        isAfterpayEnabledForThisOrder, isKlarnaEnabledForThisOrder, isPazeEnabledForThisOrder, isVenmoEnabledForThisOrder, isPayPalEnabled
    } =
        availablePaymentMethods;

    const isKlarnaSelected = orderUtils.getPaymentGroup(orderDetails, orderUtils.PAYMENT_GROUP_TYPE.KLARNA) && isKlarnaEnabledForThisOrder;
    const isAfterpaySelected = orderUtils.getPaymentGroup(orderDetails, orderUtils.PAYMENT_GROUP_TYPE.AFTERPAY) && isAfterpayEnabledForThisOrder;
    const isPazeSelected = orderUtils.getPaymentGroup(orderDetails, orderUtils.PAYMENT_GROUP_TYPE.PAZE) && isPazeEnabledForThisOrder;
    const isVenmoSelected = orderUtils.getPaymentGroup(orderDetails, orderUtils.PAYMENT_GROUP_TYPE.VENMO) && isVenmoEnabledForThisOrder;
    const isPayPalSelected = orderUtils.getPaymentGroup(orderDetails, orderUtils.PAYMENT_GROUP_TYPE.PAYPAL) && isPayPalEnabled;

    return isKlarnaSelected || isAfterpaySelected || isPazeSelected || isVenmoSelected || isPayPalSelected;
};

const payPalWithInterstice = payload => {
    return decorators.withInterstice(checkoutApi.updatePayPalCheckout, INTERSTICE_DELAY_MS)(payload, 'update');
};

// Reattaches a previously selected credit card to the order and refreshes orderDetails.
// Used when an alternate payment flow (e.g. Klarna) was opened but then canceled by the user:
// the alternate's session creation removes the CC payment group server side, so we restore it.
// Otherwise the saved CC is not shown properly in the L1 state. i.e. it disappears.
const reattachCreditCardToOrder = async creditCard => {
    const requestData = FrictionlessUtils.setRequestDataForCreditCardUpdate(creditCard);
    await checkoutApi.updateCreditCardOnOrder(requestData, 'select');
    await refreshCheckout()();
};

const getBillingCountries = () => {
    utilityApi.getCountryList().then(billingCountries => {
        store.dispatch(EditDataActions.updateEditData(billingCountries, FormsUtils.FORMS.CHECKOUT.BILLING_COUNTRIES_LIST));
    });
};

const getCreditCards = orderId => {
    return checkoutApi
        .getCreditCards(orderId)
        .then(payments => {
            store.dispatch(UtilActions.merge('order', 'paymentOptions', payments));
        })
        .catch(errorData => {
            ErrorsUtils.collectAndValidateBackEndErrors(errorData, this);
        });
};

const setAccessPoint = (accessPoint, shippingGroupId, accessPointCallback, isEdit = false) => {
    const address = {
        addressType: orderUtils.SHIPPING_METHOD_TYPES.HAL,
        address1: accessPoint?.addressLine1,
        city: accessPoint?.city,
        state: accessPoint?.state,
        postalCode: accessPoint?.zipCode,
        country: accessPoint?.country,
        altPickLocationID: accessPoint?.locationId,
        altPickLocationType: accessPoint?.pickupLocationType,
        altPickLocationPartner: accessPoint?.carrierFixed,
        altLocationPhone: accessPoint?.phoneNumber,
        halCompany: accessPoint?.companyName
    };

    const halOperatingHours = accessPoint?.operatingHours;
    CheckoutActions.createDraftHalAddress(address, shippingGroupId, halOperatingHours);
    accessPointCallback(true, isEdit);
};
const saveAndContinue = (newShippingAddress, validateAddress, setAnalyticsAndErrors, handleResponseError) => () => {
    const postalCode = formatZipCode(newShippingAddress.address.postalCode);

    updatePreferredZipCode({ postalCode })
        .then(zipCodeData => {
            const zipData = {
                preferredZipCode: zipCodeData.zipCode,
                encryptedStoreIds: zipCodeData.encryptedStoreIds
            };
            Storage.session.setItem(LOCAL_STORAGE.PREFERRED_ZIP_CODE, zipData);

            if (Sephora.configurationSettings.setZipStoreCookie) {
                cookieUtils.write('sameDayZipcodeCookie', zipCodeData.zipCode, null, false, false);
            }

            ShipAddressActions.updateShippingAddress(newShippingAddress)
                .then(data => {
                    if (data.scheduledAddrChangeMessage) {
                        store.dispatch(OrderActions.showScheduledDeliveryUnavailable(data.scheduledAddrChangeMessage[0].messages[0]));
                        const errors = setErrorsFromCheckoutAPI(data);
                        setAnalyticsAndErrors(SECTION_NAMES.SDD, errors);
                    }

                    validateAddress(newShippingAddress.address.addressId);
                })
                .catch(handleResponseError);
        })
        .catch(handleResponseError);
};

const addressApiCall = ({
    dataToSend, apiToCall, callback, isNCR, setAnalyticsAndErrors, comp
}) => {
    decorators
        .withInterstice(
            apiToCall,
            INTERSTICE_DELAY_MS
        )(dataToSend)
        .then(results => {
            if (results.scheduledAddrChangeMessage) {
                OrderActions.showScheduledDeliveryUnavailable(results.scheduledAddrChangeMessage[0].messages[0]);
                const errors = setErrorsFromCheckoutAPI(results);
                setAnalyticsAndErrors(SECTION_NAMES.SDD, errors);
            } else {
                OrderActions.clearNamedSectionErrors([SECTION_NAMES.SDD, SECTION_NAMES.DELIVER_TO]);
            }

            callback(results, dataToSend);
        })
        .catch(errorData => {
            if (errorData.errors?.sameDaySkuOOSException) {
                Storage.session.setItem(LOCAL_STORAGE.PREFERRED_ZIP_CODE, { preferredZipCode: dataToSend.address.postalCode });
            }

            const errors = setErrorsFromCheckoutAPI(errorData);
            setAnalyticsAndErrors(SECTION_NAMES.SDD, errors);

            if (isNCR) {
                const { originatingOrderId } = Storage.local.getItem(LOCAL_STORAGE.NCR_ORDER) || {};
                ReplacementOrderActions.showSessionExpiredModal(originatingOrderId);
            } else {
                errorsUtils.collectAndValidateBackEndErrors(errorData, comp);
            }
        });
};
const updatePreferredZipCodeFromForm = ({ postalCode, updatePreferredZipCodeFunction }) => {
    const formattedPostalCode = formatZipCode(postalCode);

    return updatePreferredZipCodeFunction({ postalCode: formattedPostalCode }).then(zipCodeData => {
        const zipData = {
            preferredZipCode: zipCodeData.zipCode,
            encryptedStoreIds: zipCodeData.encryptedStoreIds
        };

        Storage.session.setItem(LOCAL_STORAGE.PREFERRED_ZIP_CODE, zipData);

        if (Sephora.configurationSettings.setZipStoreCookie) {
            cookieUtils.write('sameDayZipcodeCookie', zipCodeData.zipCode, null, false, false);
        }

        return zipCodeData;
    });
};

const getAddressApi = ({ isNCR, address, _deletedFromAddressBook, apis }) => {
    const { updateAddress, shippingAddress, updateShippingAddress, createShippingAddress } = apis;

    if (isNCR && address && !_deletedFromAddressBook) {
        return updateAddress;
    } else if (isNCR) {
        return shippingAddress;
    } else if (address) {
        return updateShippingAddress;
    } else {
        return createShippingAddress;
    }
};

/**
 * Sets the selection state for each alternate payment method in Redux. Pass only the methods you
 * want to mark as selected; omitted methods are reset to `false`. Calling with no arguments resets
 * all seven flags to `false`.
 *
 * @param {Object} [selections={}]
 * @param {boolean} [selections.isApplePayFlow=false] - Mirrors the `order.isApplePayFlow` flag.
 * @param {boolean} [selections.klarna=false]         - Selects Klarna (`klarna.isSelected`).
 * @param {boolean} [selections.afterpay=false]       - Selects Afterpay (`afterpay.isSelected`).
 * @param {boolean} [selections.venmo=false]          - Selects Venmo (`venmo.isSelected`).
 * @param {boolean} [selections.paypal=false]         - Selects PayPal (`paypal.isSelected`).
 * @param {boolean} [selections.paze=false]           - Selects Paze (`paze.isSelected`).
 * @param {boolean} [selections.googlePay=false]      - Selects Google Pay (`googlePay.isSelected`).
 */
const commonOrderToggleActions = ({
    isApplePayFlow = false,
    klarna = false,
    afterpay = false,
    venmo = false,
    paypal = false,
    paze = false,
    googlePay = false
} = {}) => {
    store.dispatch(UtilActions.merge('order', 'isApplePayFlow', isApplePayFlow));
    store.dispatch(KlarnaActions.toggleSelect(klarna));
    store.dispatch(AfterpayActions.toggleSelect(afterpay));
    store.dispatch(VenmoActions.toggleSelect(venmo));
    store.dispatch(paypalToggleSelect(paypal));
    store.dispatch(PazeActions.toggleSelect(paze));
    store.dispatch(GooglePayActions.toggleSelect(googlePay));
};

const updatePaymentActions = (pazeValue = false, appleValue = false) => {
    store.dispatch(PazeActions.toggleSelect(pazeValue));
    store.dispatch(AfterpayActions.resetError());
    store.dispatch(PazeActions.resetError());
    store.dispatch(VenmoActions.resetError());
    store.dispatch(OrderActions.orderReviewIsActive(appleValue));
};

const sectionSaveOrderAction = (value, component) => {
    store.dispatch(OrderActions.sectionSaved(value, component));
};

const swapPaypalToCreditCard = () => {
    store.dispatch(OrderActions.swapPaypalToCredit());
};

const setNewCardActions = () => {
    store.dispatch(PazeActions.toggleSelect(false));
    store.dispatch(AfterpayActions.resetError());
};

export {
    refreshCheckout,
    getShippingMethods,
    getAddressList,
    checkSelectedPayment,
    payPalWithInterstice,
    getBillingCountries,
    getCreditCards,
    saveAndContinue,
    setAccessPoint,
    getAddressApi,
    updatePreferredZipCodeFromForm,
    addressApiCall,
    commonOrderToggleActions,
    updatePaymentActions,
    sectionSaveOrderAction,
    swapPaypalToCreditCard,
    setNewCardActions,
    isAlternatePaymentMethodSelected,
    reattachCreditCardToOrder
};
